import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, makeStyles } from "@material-ui/core";
import {
    IProductDataEntry,
    TLineItemIds,
    validCustomizationSelections,
} from "constants/product-customizations/customization";
import { TCustomizationSelection } from "constants/product-customizations/customization-selection.types";
import { IProduct } from "records/item/item";
import { formatMessage } from "lib/globalize-util";
import theme from "styles/theme";
import { ProductList } from "./product-list";
import { DialogHeader } from "../../dialog";
import { ProductView } from "./product-view";
import { CustomizationsView } from "./customizations-view";
import { CUSTOMIZATIONS } from "constants/integration/customizations";
import classnames from "classnames";
import me from "models/me";
import App from "lib/app";
import { connect, Dispatch } from "react-redux";
import { resetCurrentSelection } from "../../../actions/customization-actions";
import { IStore } from "reducers";

interface IEventQuery {
    _id: string[];
}

interface ITriggerParams {
    query: IEventQuery;
    type: string;
    __customizations: TCustomizationSelection[];
    __product_customizations_threedium_model: string;
    __product_customizations_planar2d_model: string;
    __product_customizations_template_id: string;
}

interface IParamsData {
    params: ITriggerParams;
}

export interface IApp {
    trigger: (event: string, params: IParamsData) => void;
    legacy: {
        sendMessage: (message: string) => void;
        onMessage: (message: string) => void;
    };
}

const app = App as IApp;

export interface IBulkCustomizationModalStateProps {
    isFetchingProducts: boolean;
}

export interface IBulkCustomizationModalProps {
    customizations: TCustomizationSelection[];
    productData?: IProductDataEntry[];
    product: IProduct;
    onCloseModal: () => void;
    isPDP: boolean;
    onApply?: (ids: TLineItemIds, customizations: TCustomizationSelection[]) => void;
    onAddToOrder?: (products: IProduct[]) => void;
}

export interface IBulkCustomizationModalDispatchProps {
    onResetCustomizationSelection: () => void;
}

export const BulkCustomizationModal: React.FC<
    IBulkCustomizationModalStateProps & IBulkCustomizationModalProps & IBulkCustomizationModalDispatchProps
> = ({
    onCloseModal,
    product,
    productData,
    customizations,
    isPDP,
    onApply,
    onResetCustomizationSelection,
    ...props
}) => {
    const classes = useStyles();
    const [selectedProductsData, setSelectedProductsData] = useState<IProductDataEntry[]>([]);

    const handleSelectedProductsChange = (productsData: IProductDataEntry[]) => {
        setSelectedProductsData(productsData);
    };

    const handleApply = () => {
        const selectedLineItemsIds = selectedProductsData.map(({ lineItem }) => lineItem?._id);

        onApply?.(selectedLineItemsIds as TLineItemIds, customizations);
        onCloseModal();
    };

    const handleAddToOrder = () => {
        const selectedProductsIds: string[] = selectedProductsData.map(({ product }) => product._id);

        const query = {
            _id: [product._id, ...selectedProductsIds],
        };

        app.trigger("add-to-cart", {
            params: {
                query: query,
                type: "items",
                __customizations: validCustomizationSelections(customizations),
                __product_customizations_threedium_model: product.get("__product_customizations_threedium_model"),
                __product_customizations_planar2d_model: product.get("__product_customizations_planar2d_model"),
                __product_customizations_template_id: product.get("__product_customizations_template_id"),
            },
        });

        me.trackAction("Add to order", {
            selectAll: false,
            resource: "bulk",
            area: "gallery",
            bulk: true,
        });

        onResetCustomizationSelection();
        onCloseModal();
    };

    return (
        <Dialog
            open={true}
            classes={{ paper: classnames(classes.dialogPaper, CUSTOMIZATIONS.BULK_CUSTOMIZATIONS_MODAL) }}
            style={styles.dialogRoot}
            onClose={onCloseModal}
            disableScrollLock={true}
        >
            <DialogHeader
                className={classes.dialogTitle}
                title={formatMessage(isPDP ? "Apply Customization to more products" : "Bulk Customizations")}
                onClose={onCloseModal}
            />
            <DialogContent className={classes.dialogContent}>
                <ProductView product={product} />
                <hr className={classes.separator} />
                {customizations.length > 0 && <CustomizationsView customizations={customizations} />}

                <ProductList
                    isPDP={isPDP}
                    cartProductsData={productData}
                    customizations={customizations}
                    onSelectedProductsChange={handleSelectedProductsChange}
                    mainProductId={product._id}
                />
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    className={classes.button}
                    style={{ marginRight: "16px" }}
                    variant="outlined"
                    onClick={onCloseModal}
                    key="close"
                    data-test={CUSTOMIZATIONS.CANCEL_BULK_CUSTOMIZATIONS_BTN}
                >
                    {formatMessage("Cancel")}
                </Button>
                <Button
                    className={classes.primaryButton}
                    onClick={isPDP ? handleAddToOrder : handleApply}
                    variant="outlined"
                    key="complete"
                    disabled={props.isFetchingProducts}
                    data-test={
                        isPDP
                            ? CUSTOMIZATIONS.ADD_BULK_CUSTOMIZATIONS_BTN
                            : CUSTOMIZATIONS.APPLY_BULK_CUSTOMIZATIONS_BTN
                    }
                >
                    {formatMessage(isPDP ? "Add to Order" : "Apply")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const mapStateToProps = (state: IStore): IBulkCustomizationModalStateProps => {
    const bulk = state.customization.bulkCustomization;

    return {
        isFetchingProducts: bulk?.isFetchingProducts,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<{}>): IBulkCustomizationModalDispatchProps => ({
    onResetCustomizationSelection: () => dispatch(resetCurrentSelection()),
});

export default connect<
    IBulkCustomizationModalStateProps,
    IBulkCustomizationModalDispatchProps,
    IBulkCustomizationModalProps
>(
    mapStateToProps,
    mapDispatchToProps
)(BulkCustomizationModal);

const styles = {
    dialogRoot: {
        zIndex: 1500,
    },
};

const useStyles = makeStyles({
    dialogPaper: {
        maxWidth: "1400px",
        width: "90vw",
        minHeight: "75vh",
        height: "100%",
        transform: "none",
    },
    dialogActions: {
        padding: "20px 24px",
    },
    dialogContent: {
        padding: "10px 40px",
    },
    dialogTitle: {
        fontSize: 18,
        fontWeight: "bold",
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
        padding: "20px",
    },
    separator: {
        border: "none",
        height: "1px",
        backgroundColor: "#DDD",
        margin: "5px 0 10px 0",
    },
    button: {
        borderRadius: "20px",
        fontSize: "14px",
        padding: "6px 32px",
        textTransform: "none",
    },
    primaryButton: {
        border: `1px solid ${theme.palette.primary1Color}`,
        backgroundColor: theme.palette.primary1Color,
        borderRadius: "20px",
        color: theme.palette.white,
        fontSize: "14px",
        padding: "6px 32px",
        textTransform: "none",
        "&:hover": {
            backgroundColor: "#2dbeb4",
        },
        "&:disabled": {
            color: theme.palette.white,
            backgroundColor: "#8ccac6",
        },
    },
});
